import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

const API_BASE = '/api/v2/euchre'

function parseFlag(value: string | undefined): boolean {
  if (!value) return false
  const normalized = value.trim().toLowerCase()
  return normalized === '1' || normalized === 'true' || normalized === 'yes'
}

const fakeAuthEnabled = parseFlag(import.meta.env.VITE_EUCHRE_FAKE_AUTH)

export interface FakeLoginPayload {
  email: string
  name?: string
  picture?: string
  isAdmin?: boolean
}

export interface User {
  email: string
  name: string
  picture: string
}

export interface Session {
  expiresAt: string
  lastActivityAt?: string
  isAdmin?: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const session = ref<Session | null>(null)
  const isLoading = ref(false)

  const isAuthenticated = computed(() => user.value !== null)
  const isAdmin = computed(() => session.value?.isAdmin === true)

  async function checkSession() {
    try {
      isLoading.value = true
      const response = await fetch(`${API_BASE}/auth/session`)
      
      if (response.ok) {
        const data = await response.json()
        if (data.authenticated) {
          user.value = data.user
          session.value = data.session
          return true
        }
      }
      
      user.value = null
      session.value = null
      return false
    } catch (error) {
      console.error('Failed to check session:', error)
      user.value = null
      session.value = null
      return false
    } finally {
      isLoading.value = false
    }
  }

  function login(returnTo: string = '/lobby') {
    const loginUrl = `${API_BASE}/auth/google/login?returnTo=${encodeURIComponent(returnTo)}`
    window.location.href = loginUrl
  }

  function goToDevLogin(returnTo: string = '/lobby') {
    const url = new URL('/dev-login', window.location.origin)
    url.searchParams.set('returnTo', returnTo)
    window.location.href = url.toString()
  }

  async function logout() {
    try {
      isLoading.value = true

      let response = await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST'
      })

      if (!response.ok && fakeAuthEnabled) {
        response = await fetch(`${API_BASE}/auth/fake-logout`, {
          method: 'POST'
        })
      }

      if (!response.ok) {
        throw new Error(`Logout failed with status ${response.status}`)
      }
      
      user.value = null
      session.value = null
      
      // Redirect to home
      window.location.href = '/'
    } catch (error) {
      console.error('Failed to logout:', error)
    } finally {
      isLoading.value = false
    }
  }

  async function fakeLogin(payload: FakeLoginPayload) {
    if (!fakeAuthEnabled) {
      throw new Error('Fake authentication is not enabled in this environment')
    }

    try {
      isLoading.value = true
      const response = await fetch(`${API_BASE}/auth/fake-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        throw new Error(`Fake login failed with status ${response.status}`)
      }

      const data = await response.json()
      user.value = data.user
      session.value = data.session
      return data
    } finally {
      isLoading.value = false
    }
  }

  return {
    user,
    session,
    isLoading,
    isAuthenticated,
    isAdmin,
    fakeAuthEnabled,
    checkSession,
    login,
    goToDevLogin,
    logout,
    fakeLogin
  }
})
